// src/components/Auth/SignupPage.js
import React, { useState, useEffect } from 'react'; // Import useEffect
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAuth } from 'firebase/auth';
import PasswordStrengthIndicator from './PasswordStrengthIndicator'; // Import the new component
import { getPasswordValidationState } from '../../utils/validation'; // Import the validation utility
import './SignupPage.css'; // Keep existing CSS

const SignupPage = () => {
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(null); // State for password validation results
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false); // Control visibility

  const { signup, sendEmailVerification, saveUserProfile } = useAuth();
  const navigate = useNavigate();

  // Update password validation state whenever password changes
  useEffect(() => {
    if (password) {
      // Only validate if password is not empty
      const validation = getPasswordValidationState(password);
      setPasswordValidation(validation);
      setShowPasswordRequirements(true); // Show requirements when user types
    } else {
      setPasswordValidation(null); // Clear validation if password empty
      setShowPasswordRequirements(false); // Hide if empty
    }
  }, [password]);

  const handlePasswordFocus = () => {
    if (!password) {
      // Show requirements on focus only if field is empty
      setShowPasswordRequirements(true);
    }
  };

  const handlePasswordBlur = () => {
    if (!password) {
      // Hide requirements on blur only if field is empty
      setShowPasswordRequirements(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setMessage(
      'At the moment Sign Up is not available. Please check back later.',
    );
    return;

    // --- Validation ---
    if (!userName.trim()) {
      setError('Username is required.');
      return;
    }

    const currentPasswordValidation = getPasswordValidationState(password); // Re-validate on submit
    if (!currentPasswordValidation.isValid) {
      setError('Password does not meet all requirements.');
      setPasswordValidation(currentPasswordValidation); // Ensure indicator shows errors
      setShowPasswordRequirements(true); // Keep indicator visible
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // --- End Validation ---

    setLoading(true);
    try {
      const result = await signup(email, password);
      const user = result.user;

      if (user) {
        await sendEmailVerification(user);
        const userData = {
          userName: userName,
          email: user.email,
          photoURL: user.photoURL || '',
          role: 'Manager', // Default role
          SignUpDate: new Date().toISOString(),
        };
        await saveUserProfile(userData);

        setVerificationSent(true);
        setMessage(
          'Signup successful! Please check your email to verify your account.',
        );
        // Clear form fields after success? Optional.
        setUserName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setPasswordValidation(null);
      } else {
        setError('User not found after signup.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError(
          'Email is already in use. Please log in or use a different email.',
        );
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak or does not meet requirements.');
      } else if (error.code === 'auth/network-request-failed') {
        setError(
          'Your network connection was lost. Please check your internet connection and try again.',
        );
      } else {
        setError(`Signup failed: ${error.message}`); // Provide more context
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError(''); // Clear previous errors
    setMessage(''); // Clear previous messages
    try {
      const auth = getAuth();
      // Try to get the user directly after signup might fail if state hasn't updated
      // It's safer to rely on the fact that signup *should* have set the currentUser
      const user = auth.currentUser;
      if (user && !user.emailVerified) {
        // Check if user exists and is not verified
        await sendEmailVerification(user); // Use context function for consistency
        setMessage(
          'Verification email resent! Please check your inbox (and spam folder).',
        );
      } else if (user && user.emailVerified) {
        setMessage('Your email is already verified. You can now log in.');
        // Optionally navigate to login after a delay
        setTimeout(() => navigate('/login'), 3000);
      } else {
        // This case might happen if the component state is lost or user signed out
        setError(
          'Could not find user session. Please try logging in or signing up again.',
        );
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setError('Failed to resend verification email. ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Render Logic ---

  if (verificationSent) {
    return (
      <div className="verify-email-page">
        {' '}
        {/* Use a specific class */}
        <h2>Verify Your Email</h2>
        <p>
          A verification email has been sent to <strong>{email}</strong>. Please
          click the link in the email to complete your registration.
        </p>
        <p className="info-text">
          If you don't see the email, please check your spam or junk folder.
        </p>
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
        <button
          onClick={handleResendVerification}
          disabled={loading}
          className="resend-button"
        >
          {loading ? 'Sending...' : 'Resend Verification Email'}
        </button>
        <button onClick={() => navigate('/login')} className="back-button">
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-form-container">
        <h2>Create Account</h2>
        <form onSubmit={handleSubmit} className="signup-form" noValidate>
          {' '}
          {/* Add noValidate */}
          {error && <p className="error-message">{error}</p>}
          {/* Username */}
          <div className="form-group">
            <label htmlFor="signup-username">Username</label>
            <input
              id="signup-username"
              type="text"
              placeholder="Choose a username"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
              aria-describedby={
                error && error.includes('Username')
                  ? 'username-error'
                  : undefined
              }
            />
            {error && error.includes('Username') && (
              <p id="username-error" className="inline-error">
                {error}
              </p>
            )}
          </div>
          {/* Email */}
          <div className="form-group">
            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-describedby={
                error && (error.includes('Email') || error.includes('email'))
                  ? 'email-error'
                  : undefined
              }
            />
            {error && (error.includes('Email') || error.includes('email')) && (
              <p id="email-error" className="inline-error">
                {error}
              </p>
            )}
          </div>
          {/* Password */}
          <div className="form-group">
            <label htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              type="password"
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={handlePasswordFocus} // Show requirements on focus
              onBlur={handlePasswordBlur} // Hide if empty on blur
              required
              aria-describedby={
                showPasswordRequirements
                  ? 'password-reqs'
                  : error && error.includes('Password')
                    ? 'password-error'
                    : undefined
              }
            />
            {/* Conditionally render the strength indicator */}
            {showPasswordRequirements && (
              <div id="password-reqs">
                <PasswordStrengthIndicator
                  validationState={passwordValidation}
                />
              </div>
            )}
            {error &&
              error.includes('Password') &&
              !showPasswordRequirements && (
                <p id="password-error" className="inline-error">
                  {error}
                </p>
              )}
          </div>
          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="signup-confirm-password">Confirm Password</label>
            <input
              id="signup-confirm-password"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              aria-describedby={
                error && error.includes('match')
                  ? 'confirm-password-error'
                  : undefined
              }
            />
            {error && error.includes('match') && (
              <p id="confirm-password-error" className="inline-error">
                {error}
              </p>
            )}
          </div>
          {/* General error message area (for non-field specific errors) */}
          {error &&
            !(
              error.includes('Username') ||
              error.includes('Email') ||
              error.includes('email') ||
              error.includes('Password') ||
              error.includes('match')
            ) && <p className="error-message">{error}</p>}
          {message && <p className="success-message">{message}</p>}
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
          <div className="additional-actions">
            <span>Already have an account? </span>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="link-button"
            >
              Log In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
